import { create } from 'zustand';

/**
 * The admin's bilingual layer.
 *
 * Arabic-first, with English one step away. The menu shows the active
 * language only, so it stays short, and each entry carries the other language
 * as its tooltip, for the term someone only knows in English.
 */

export const LOCALES = ['ar', 'en'] as const;
export type AdminLocale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: AdminLocale = 'ar';

export const DIRECTION: Record<AdminLocale, 'rtl' | 'ltr'> = { ar: 'rtl', en: 'ltr' };

const LOCALE_KEY = 'ecom_admin_locale';
const THEME_KEY = 'ecom_admin_theme';
const MENU_KEY = 'ecom_admin_menu';

export type ThemeChoice = 'light' | 'dark' | 'system';

/** A label that always carries both languages — see the note above. */
export interface BilingualLabel {
  ar: string;
  en: string;
}

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return allowed.includes(stored as T) ? (stored as T) : fallback;
  } catch {
    return fallback;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be blocked; the choice then lasts for this tab only.
  }
}

/** Applies language and theme to the document. Direction is a document concern. */
export function applyDocument(locale: AdminLocale, theme: ThemeChoice): void {
  const root = document.documentElement;

  root.lang = locale;
  root.dir = DIRECTION[locale];

  // `system` removes the attribute so the media query in the theme takes over.
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.dataset.theme = theme;
}

interface UiState {
  locale: AdminLocale;
  theme: ThemeChoice;
  setLocale: (locale: AdminLocale) => void;
  setTheme: (theme: ThemeChoice) => void;
  /** Cycles light → dark → system, which is what a single toggle button needs. */
  cycleTheme: () => void;
  /** The sidebar folded to its icons — remembered, like the language and the theme. */
  menuCollapsed: boolean;
  setMenuCollapsed: (collapsed: boolean) => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  locale: readStored(LOCALE_KEY, LOCALES, DEFAULT_LOCALE),
  theme: readStored(THEME_KEY, ['light', 'dark', 'system'] as const, 'system'),
  menuCollapsed: readStored(MENU_KEY, ['collapsed', 'expanded'] as const, 'expanded') === 'collapsed',

  setLocale(locale) {
    persist(LOCALE_KEY, locale);
    applyDocument(locale, get().theme);
    set({ locale });
  },

  setTheme(theme) {
    persist(THEME_KEY, theme);
    applyDocument(get().locale, theme);
    set({ theme });
  },

  cycleTheme() {
    const order: ThemeChoice[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(get().theme) + 1) % order.length];
    get().setTheme(next);
  },

  setMenuCollapsed(collapsed) {
    persist(MENU_KEY, collapsed ? 'collapsed' : 'expanded');
    set({ menuCollapsed: collapsed });
  },
}));

/** Picks the side of a bilingual label matching the active language. */
export function label(value: BilingualLabel, locale: AdminLocale): string {
  return value[locale];
}

/** The other language, for the toggle's own label. */
export function otherLocale(locale: AdminLocale): AdminLocale {
  return locale === 'ar' ? 'en' : 'ar';
}
